<template>
  <div class="backlog-container">
    <!-- Header -->
    <v-container fluid class="pa-4">
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex justify-space-between align-center">
            <div>
              <div class="backlog-page-title">Backlog &amp; Sprints</div>
              <div class="backlog-page-sub">Gestión de sprints y tareas</div>
            </div>
            <div class="d-flex align-center" style="gap: 10px">
              <!-- Stats toggle -->
              <button class="stats-toggle-btn" @click="showSprintStats = !showSprintStats">
                <v-icon size="15" style="color: #64748b">mdi-chart-donut</v-icon>
                <span>Estadísticas</span>
                <v-icon size="14" style="color: #94a3b8; transition: transform 0.25s" :style="showSprintStats ? 'transform:rotate(180deg)' : ''">mdi-chevron-down</v-icon>
              </button>
              <v-btn
                color="primary"
                depressed
                class="rounded-lg"
                style="letter-spacing:0.3px; font-weight:600"
                @click="openSprintDialog()"
              >
                <v-icon left small>mdi-plus</v-icon>
                Crear Sprint
              </v-btn>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Sprint Statistics (collapsible) -->
      <v-expand-transition>
        <div v-show="showSprintStats">
          <div class="stats-strip mb-5">
            <div class="stat-pill stat-pill--blue">
              <v-icon size="16" color="#3b82f6">mdi-format-list-bulleted</v-icon>
              <span class="stat-pill__value">{{ sprintStats.planned }}</span>
              <span class="stat-pill__label">Planificados</span>
            </div>
            <div class="stat-pill stat-pill--amber">
              <v-icon size="16" color="#f59e0b">mdi-lightning-bolt</v-icon>
              <span class="stat-pill__value">{{ sprintStats.active }}</span>
              <span class="stat-pill__label">Activos</span>
            </div>
            <div class="stat-pill stat-pill--green">
              <v-icon size="16" color="#22c55e">mdi-check-circle-outline</v-icon>
              <span class="stat-pill__value">{{ sprintStats.completed }}</span>
              <span class="stat-pill__label">Completados</span>
            </div>
            <div class="stat-pill stat-pill--violet">
              <v-icon size="16" color="#8b5cf6">mdi-star-outline</v-icon>
              <span class="stat-pill__value">{{ sprintStats.totalPoints }}</span>
              <span class="stat-pill__label">Puntos totales</span>
            </div>
          </div>
        </div>
      </v-expand-transition>

      <!-- Sprints by Status -->
      <v-row>
        <!-- Planificados -->
        <v-col cols="12" md="6">
          <div class="sprint-panel">
            <div class="column-header" style="border-left: 4px solid #2196f3">
              <v-icon small class="mr-2">mdi-list-box-outline</v-icon>
              <span class="font-weight-bold">Planificados</span>
              <v-chip small color="info" class="ml-2">{{
                sprintsByStatus.planned.length
              }}</v-chip>
            </div>
            <div class="sprint-list sprint-list-tall">
              <v-card
                v-for="sprint in sprintsByStatus.planned"
                :key="sprint.TspID"
                class="sprint-card-droppable mb-3 rounded-lg"
                elevation="2"
              >
                <v-card-title class="pa-3 pb-2">
                  <div class="sprint-title-header">
                    <div class="font-weight-bold">{{ sprint.TspName }}</div>
                    <div class="sprint-dates text-caption mt-1">
                      <v-icon x-small>mdi-calendar-range</v-icon>
                      {{ formatDate(sprint.TspStartDate) }} -
                      {{ formatDate(sprint.TspEndDate) }}
                    </div>
                  </div>
                </v-card-title>

                <v-divider></v-divider>

                <!-- Drop Zone para tareas -->
                <v-card-text class="pa-3">
                  <draggable
                    v-model="sprint.tasks"
                    group="tasks"
                    class="task-drop-zone"
                    ghost-class="ghost"
                    :animation="200"
                    @change="onTaskDrop($event, sprint)"
                    :scroll="true"
                :scrollSensitivity="60"
                :scrollSpeed="10"
                :forceFallback="true"
                :fallbackOnBody="true"
                  >
                    <v-card
                      v-for="task in sprint.tasks"
                      :key="task.TskID"
                      class="task-card-sprint mb-2 rounded"
                      elevation="1"
                    >
                      <v-card-text class="pa-2">
                        <div class="d-flex align-center">
                          <v-icon x-small color="grey" class="mr-1"
                            >mdi-drag</v-icon
                          >
                          <div class="flex-grow-1">
                            <div class="d-flex align-center mb-1">
                              <v-chip
                                x-small
                                color="grey"
                                outlined
                                class="mr-1"
                              >
                                #{{ task.TskID }}
                              </v-chip>
                              <v-chip
                                x-small
                                :color="task.TskStatusColor"
                                text-color="white"
                              >
                                {{ task.TskStatusName }}
                              </v-chip>
                            </div>
                            <div class="font-weight-medium text-caption">
                              {{ task.TskTitle || "Sin título" }}
                            </div>
                          </div>
                          <v-btn
                            icon
                            x-small
                            color="error"
                            @click="removeTaskFromSprint(task, sprint)"
                          >
                            <v-icon x-small>mdi-close</v-icon>
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </draggable>
                  <div
                    v-if="!sprint.tasks || sprint.tasks.length === 0"
                    class="drop-zone-empty"
                  >
                    <v-icon color="grey lighten-1">mdi-tray-arrow-down</v-icon>
                    <div class="text-caption grey--text mt-1">
                      Arrastra tareas aquí
                    </div>
                  </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-2">
                  <v-chip small color="primary" outlined>
                    {{ sprint.TspStoryPoints }} pts
                  </v-chip>
                  <v-chip small outlined class="ml-2">
                    {{ sprint.tasks ? sprint.tasks.length : 0 }} tareas
                  </v-chip>
                  <v-spacer></v-spacer>
                  <v-btn
                    icon
                    x-small
                    color="warning"
                    @click="editSprint(sprint)"
                    title="Editar"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    color="success"
                    @click="activateSprint(sprint)"
                    title="Activar Sprint"
                  >
                    <v-icon>mdi-play-circle</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    color="error"
                    @click="deleteSprint(sprint)"
                    title="Eliminar"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div class="sprint-panel-backlog">
            <v-card flat outlined class="backlog-card rounded-xl">
              <!-- Header -->
              <div class="backlog-header d-flex align-center px-4 py-3">
                <div class="d-flex align-center gap-2">
                  <v-icon small color="#94a3b8">mdi-layers-outline</v-icon>
                  <span class="backlog-title">Backlog</span>
                  <v-chip
                    x-small
                    label
                    color="#f1f5f9"
                    text-color="#64748b"
                    class="ml-1 count-chip"
                  >
                    {{ filteredUnassignedTasks.length }}
                  </v-chip>
                </div>
                <v-spacer></v-spacer>
                <span class="backlog-sub mr-3">Tareas sin asignar</span>
                <v-btn
                  color="primary"
                  small
                  outlined
                  @click="showQuickAdd = !showQuickAdd"
                >
                  <v-icon left small>{{ showQuickAdd ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
                  {{ showQuickAdd ? 'Ocultar' : 'Agregar' }}
                </v-btn>
              </div>

              <v-divider></v-divider>

              <!-- Filters -->
              <v-expand-transition>
                <div v-show="!showQuickAdd">
                  <div class="px-4 py-3 d-flex align-center gap-2" style="background-color: #fafafa;">
                    <s-select
                      v-model="filterProjectId"
                      :items="[{TkpID: null, TkpTitle: 'Todos los proyectos'}, ...projectOptions]"
                      item-text="TkpTitle"
                      item-value="TkpID"
                      label="Proyecto"
                      class="flex-grow-1"
                    ></s-select>
                    <s-select-assigned
                      full
                      return-object
                      label="Responsable"
                      clearable
                      v-model="filterPrsID"
                      class="flex-grow-1 ml-2"
                    ></s-select-assigned>
                    <s-text
                      v-model="searchTitle"
                      prepend-inner-icon="mdi-magnify"
                      label="Buscar tarea..."
                      clearable
                      class="flex-grow-1 ml-2"
                    ></s-text>
                  </div>
                  <v-divider></v-divider>
                </div>
              </v-expand-transition>

              <!-- Quick add task -->
              <v-expand-transition>
              <v-card-text v-show="showQuickAdd" class="pa-3 pb-0 pt-3" style="background-color: #f1f5f9;">
                <v-form
                  ref="quickTaskForm"
                  v-model="quickTaskValid"
                  @submit.prevent="createQuickTask"
                >
                  <div class="quick-add d-flex align-center mb-2">
                    <s-select
                      v-model="quickTaskProjectId"
                      :items="projectOptions"
                      item-text="TkpTitle"
                      item-value="TkpID"
                      label="Proyecto"
                      class="quick-add-select"
                      :loading="loadingProjects"
                    ></s-select>
                    <s-text
                      v-model="quickTaskTitle"
                      label="Nueva tarea"
                      class="quick-add-input"
                      @keyup.enter="createQuickTask"
                    ></s-text>
                    <v-btn
                      color="primary"
                      class="ml-2"
                      :loading="loadingQuickTask"
                      :disabled="!quickTaskTitle"
                      @click="createQuickTask"
                    >
                      Crear
                    </v-btn>
                  </div>
                </v-form>
              </v-card-text>
              </v-expand-transition>

              <v-divider></v-divider>

              <!-- Task List -->
              <v-card-text class="pa-3">
                <draggable
                  :list="filteredUnassignedTasks"
                  :group="{ name: 'tasks', pull: 'clone', put: false }"
                  :sort="false"
                  :handle="'.drag-handle'"
                  :scroll="true"
                  :scrollSensitivity="60"
                  :scrollSpeed="10"
                  :forceFallback="true"
                  :fallbackOnBody="true"
                  class="task-rows"
                  ghost-class="ghost"
                  :animation="150"
                >
                  <div
                    v-for="task in filteredUnassignedTasks"
                    :key="task.TskID"
                    class="task-item"
                  >
                    <div
                      class="task-row d-flex align-center px-3 py-2 rounded-lg mb-1"
                      :class="{ 'drop-target': dropTargetId === task.TskID }"
                      @dragover.prevent="onSubtaskDragOver(task)"
                      @dragenter.prevent="onSubtaskDragEnter(task)"
                      @dragleave="onSubtaskDragLeave(task)"
                      @drop="onSubtaskDrop(task)"
                    >
                      <!-- Drag handle -->
                      <v-icon size="14" color="#cbd5e1" class="drag-handle mr-3">
                        mdi-dots-grid
                      </v-icon>

                      <!-- Status dot -->
                      <span
                        class="status-dot mr-3"
                        :style="{ background: task.TskStatusColor }"
                      ></span>

                      <!-- Title -->
                      <span class="task-title flex-grow-1 text-truncate">
                        {{ task.TskTitle || "Sin título" }}
                      </span>

                      <!-- Project -->
                      <v-select
                        :value="task.TkpID"
                        :items="projectOptions"
                        item-text="TkpTitle"
                        item-value="TkpID"
                        dense
                        outlined
                        placeholder="Proyecto"
                        hide-details
                        class="task-project-select mx-3"
                        :loading="loadingProjects"
                        :disabled="savingProjectIds.includes(task.TskID)"
                        @change="updateTaskProject(task, $event)"
                      ></v-select>

                      <!-- ID badge -->
                      <span class="task-id">#{{ task.TskID }}</span>
                      <!-- bagde points -->
                      <v-chip
                        x-small
                        color="primary"
                        text-color="white"
                        class="ml-2 points-badge"
                        v-if="getTaskPointsValue(task) > 0"
                      >
                        {{ getTaskPointsText(task) }}
                      </v-chip>
                      <v-btn
                        icon
                        x-small
                        class="ml-1"
                        @click.stop="openTaskDialog(task)"
                        title="Editar"
                      >
                        <v-icon x-small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        class="ml-1"
                        @click.stop="toggleSubtasks(task)"
                        :title="getSubtaskToggleTitle(task)"
                      >
                        <v-icon x-small>
                          {{ isSubtasksOpen(task) ? "mdi-chevron-up" : "mdi-chevron-down" }}
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        class="ml-1"
                        :disabled="isTaskDragDisabled(task)"
                        title="Arrastrar como subtarea"
                      >
                        <v-icon
                          x-small
                          class="subtask-drag-handle"
                          :draggable="!isTaskDragDisabled(task)"
                          @dragstart="onSubtaskDragStart(task)"
                          @dragend="onSubtaskDragEnd"
                        >
                          mdi-source-branch
                        </v-icon>
                      </v-btn>
                    </div>

                    <v-expand-transition>
                      <div
                        v-if="isSubtasksOpen(task)"
                        class="subtasks-panel mb-2 ml-6"
                      >
                        <div
                          v-if="loadingSubtaskIds.includes(task.TskID)"
                          class="subtasks-loading"
                        >
                          <v-progress-linear
                            indeterminate
                            color="primary"
                            height="3"
                          ></v-progress-linear>
                        </div>
                        <div v-else>
                          <div
                            v-if="getSubtasks(task).length === 0"
                            class="subtasks-empty"
                          >
                            Sin subtareas
                          </div>
                          <div
                            v-else
                            v-for="subtask in getSubtasks(task)"
                            :key="subtask.TskID"
                            class="subtask-row d-flex align-center"
                          >
                            <v-icon x-small class="mr-2" color="#94a3b8">
                              mdi-subdirectory-arrow-right
                            </v-icon>
                            <span class="subtask-title flex-grow-1">
                              {{ subtask.TskTitle || "Sin título" }}
                            </span>
                            <span class="subtask-id mr-2">#{{ subtask.TskID }}</span>
                            <v-btn
                              icon
                              x-small
                              title="Quitar como subtarea"
                              @click.stop="removeSubtaskParent(subtask, task)"
                            >
                              <v-icon x-small>mdi-link-off</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </div>
                    </v-expand-transition>
                  </div>
                </draggable>

                <!-- Empty state -->
                <div
                  v-if="filteredUnassignedTasks.length === 0"
                  class="empty-state py-5 text-center"
                >
                  <v-icon color="#e2e8f0" size="28"
                    >mdi-check-circle-outline</v-icon
                  >
                  <div class="empty-text mt-1">
                    {{ unassignedTasks.length === 0 ? 'Todas las tareas están asignadas' : 'No hay tareas que coincidan con la búsqueda' }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col cols="12">
          <div class="sprint-panel mb-4">
            <div class="column-header" style="border-left: 4px solid #ff9800">
              <v-icon small class="mr-2">mdi-run</v-icon>
              <span class="font-weight-bold">Activos</span>
              <v-chip small color="warning" class="ml-2">{{
                sprintsByStatus.active.length
              }}</v-chip>
            </div>
            <div class="sprint-list sprint-list-medium">
              <v-card
                v-for="sprint in sprintsByStatus.active"
                :key="sprint.TspID"
                class="sprint-card mb-3 rounded-lg active-sprint"
                elevation="2"
                @click="selectSprint(sprint)"
              >
                <v-card-text>
                  <div class="sprint-title font-weight-bold mb-2">
                    {{ sprint.TspName }}
                  </div>
                  <div class="sprint-badge mb-2">
                    <v-chip small color="warning" text-color="white">
                      <v-icon x-small left>mdi-lightning-bolt</v-icon>
                      EN PROGRESO
                    </v-chip>
                  </div>
                  <div class="sprint-dates text-caption mb-3">
                    <v-icon x-small>mdi-calendar-range</v-icon>
                    {{ formatDate(sprint.TspStartDate) }} -
                    {{ formatDate(sprint.TspEndDate) }}
                  </div>
                  <div class="sprint-points mb-3">
                    <v-chip small color="primary" outlined>
                      {{ sprint.TspStoryPoints }} pts
                    </v-chip>
                  </div>
                  <div class="progress-bar mb-2">
                    <v-progress-linear
                      :value="50"
                      color="warning"
                      height="6"
                      class="rounded"
                    ></v-progress-linear>
                  </div>
                  <div class="sprint-actions" style="display: flex; gap: 8px">
                    <v-btn
                      x-small
                      color="success"
                      @click.stop="completeSprint(sprint)"
                      icon
                      title="Completar Sprint"
                    >
                      <v-icon>mdi-check-circle</v-icon>
                    </v-btn>
                    <v-btn
                      x-small
                      color="info"
                      @click.stop="viewSprintTasks(sprint)"
                      icon
                      title="Ver Tareas"
                    >
                      <v-icon>mdi-list-box</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>

          <div class="sprint-panel">
            <div class="column-header" style="border-left: 4px solid #4caf50">
              <v-icon small class="mr-2">mdi-check-circle</v-icon>
              <span class="font-weight-bold">Completados</span>
              <v-chip small color="success" class="ml-2">{{
                sprintsByStatus.completed.length
              }}</v-chip>
            </div>
            <div class="sprint-list sprint-list-compact">
              <v-card
                v-for="sprint in sprintsByStatus.completed"
                :key="sprint.TspID"
                class="sprint-card mb-3 rounded-lg completed-sprint"
                elevation="1"
                >
                <!-- @click="selectSprint(sprint)" -->
                <v-card-text>
                  <div class="sprint-title font-weight-bold mb-2">
                    {{ sprint.TspName }}
                  </div>
                  <div class="sprint-dates text-caption mb-3">
                    <v-icon x-small>mdi-calendar-range</v-icon>
                    {{ formatDate(sprint.TspStartDate) }} -
                    {{ formatDate(sprint.TspEndDate) }}
                  </div>
                  <div class="sprint-points mb-3">
                    <v-chip small color="success" outlined>
                      {{ sprint.TspStoryPoints }} pts
                    </v-chip>
                  </div>
                  <div class="sprint-actions">
                    <v-btn
                      x-small
                      color="info"
                      @click.stop="viewSprintDetails(sprint)"
                      icon
                      title="Ver Detalles"
                      block
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Dialog for Create/Edit Sprint -->
    <v-dialog v-model="dialogSprint" max-width="600px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="primary white--text rounded-t-lg">
          <v-icon left>{{
            editingSprintId ? "mdi-pencil" : "mdi-plus"
          }}</v-icon>
          {{ editingSprintId ? "Editar Sprint" : "Crear Nuevo Sprint" }}
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="sprintForm" v-model="formValid">
            <v-text-field
              v-model="newSprint.TspName"
              label="Nombre del Sprint"
              placeholder="Ej: Sprint 1 - Enero 2026"
              outlined
              dense
              class="mb-4"
              :rules="[(v) => !!v || 'El nombre es requerido']"
            ></v-text-field>

            <v-row>
              <v-col cols="12" md="6">
                <s-date
                  v-model="newSprint.TspStartDate"
                  label="Fecha de Inicio"
                ></s-date>
              </v-col>
              <v-col cols="12" md="6">
                <s-date
                  v-model="newSprint.TspEndDate"
                  label="Fecha de Fin"
                ></s-date>
              </v-col>
            </v-row>

            <s-text
              v-model.number="newSprint.TspStoryPoints"
              label="Puntos de Historia"
              type="number"
              step="0.5"
              outlined
              dense
              class="mb-4"
              :rules="[(v) => v > 0 || 'Los puntos deben ser mayores a 0']"
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="closeSprintDialog()" color="gray" outlined>
            Cancelar
          </v-btn>
          <v-btn
            @click="saveSprint()"
            color="primary"
            :loading="loadingSave"
            :disabled="!formValid"
          >
            {{ editingSprintId ? "Actualizar" : "Crear" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog for Sprint Tasks -->
    <v-dialog v-model="dialogSprintTasks" max-width="800px">
      <v-card class="rounded-lg">
        <v-card-title class="primary white--text">
          Tareas del Sprint: {{ selectedSprint?.TspName }}
        </v-card-title>

        <v-card-text class="pa-6">
          <v-data-table
            :headers="taskHeaders"
            :items="sprintTasks"
            dense
            :loading="loadingTasks"
          >
            <template v-slot:item.TskStatus="{ item }">
              <v-chip small :color="getStatusColor(item.TskStatus)">
                {{ item.TskStatusName }}
              </v-chip>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogSprintTasks = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog for Task Edit -->
    <v-dialog v-model="dialogTaskEdit" max-width="700px" persistent>
      <v-card class="rounded-lg">
        <v-card-title class="primary white--text rounded-t-lg">
          <v-icon left>mdi-pencil</v-icon>
          Editar tarea
        </v-card-title>

        <v-card-text class="pa-6">
          <v-form ref="taskForm" v-model="taskFormValid">
            <v-text-field
              v-model="editTask.TskTitle"
              label="Titulo"
              outlined
              dense
              class="mb-4"
              :rules="[(v) => !!v || 'El titulo es requerido']"
            ></v-text-field>

            <v-textarea
              v-model="editTask.TskDescription"
              label="Descripcion"
              outlined
              dense
              rows="3"
              class="mb-4"
            ></v-textarea>

            <v-row>
              <v-col cols="12" md="4">
                <s-text
                  v-model.number="editTask.TskStoryPoints"
                  label="Puntos de Historia"
                  type="number"
                  step="0.5"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="4">
                <s-select-definition
                  label="Tipo de Tarea"
                  :def="1476"
                  v-model="editTask.TypeTask"
                ></s-select-definition>
              </v-col>
              <v-col cols="12" md="4">
                <s-select-definition
                  label="Prioridad"
                  :def="1392"
                  v-model="editTask.TskPriority"
                ></s-select-definition>
              </v-col>
            </v-row>

            <v-file-input
              v-model="editTaskFile"
              label="Adjunto"
              outlined
              dense
              show-size
              class="mt-2"
            ></v-file-input>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="closeTaskDialog" color="gray" outlined>
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="loadingTaskSave"
            :disabled="!taskFormValid"
            @click="saveTask"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      bottom
      right
      auto-close
    >
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import _sTicSprintService from "@/services/InformationTechnology/TicSprintService.js";
import _sTaskService from "@/services/InformationTechnology/TaskService.js";
import _sGeneral from "@/services/HelperService.js";
import sSelectAssigned from '@/components/Utils/SSelectAssigned.vue';
import draggable from "vuedraggable";

export default {
  name: "Backlog",
  components: {
    draggable,
    sSelectAssigned,
  },
  data() {
    return {
      // Sprints data
      sprints: [],
      selectedSprint: null,
      sprintTasks: [],

      // Tasks data
      tasks: [],
      unassignedTasks: [],

      // Sprint statistics visibility
      showSprintStats: false,

      // Quick add task
      showQuickAdd: false,
      quickTaskTitle: "",
      quickTaskValid: false,
      quickTaskProjectId: null,
      loadingQuickTask: false,
      
      // Filters
      filterProjectId: null,
      filterPrsID: null,
      searchTitle: "",

      // Dialog states
      dialogSprint: false,
      dialogSprintTasks: false,
      dialogTaskEdit: false,

      // Form states
      formValid: false,
      taskFormValid: false,
      editingSprintId: null,
      editTask: {},
      editTaskFile: null,
      newSprint: {
        TspID: null,
        TspName: "",
        TspStartDate: new Date().toISOString().split("T")[0],
        TspEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        TspStatus: 1,
        TspStoryPoints: 0,
      },

      // Loading states
      loadingSave: false,
      loadingTasks: false,
      loadingSprints: false,
      loadingTaskSave: false,

      // Projects
      projectOptions: [],
      loadingProjects: false,
      savingProjectIds: [],

      // Subtasks
      subtasksByParent: {},
      expandedTaskIds: [],
      loadingSubtaskIds: [],
      draggingTask: null,
      dropTargetId: null,

      // Options
      sprintStatusOptions: [
        { text: "Planificado", value: 1 },
        { text: "Activo", value: 2 },
        { text: "Completado", value: 3 },
      ],

      // Table headers for tasks
      taskHeaders: [
        { text: "ID", value: "TskID" },
        { text: "Título", value: "TskTitle" },
        { text: "Estado", value: "TskStatus" },
        { text: "Prioridad", value: "TskPriority" },
      ],

      // Snackbar
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },

  computed: {
    filteredUnassignedTasks() {
      return this.unassignedTasks.filter((t) => {
        let matchProject = true;
        let matchTitle = true;
        let matchResponsible = true;
        
        if (this.filterProjectId !== null) {
          matchProject = t.TkpID === this.filterProjectId;
        }
        
        if (this.searchTitle) {
          matchTitle = (t.TskTitle || "").toLowerCase().includes(this.searchTitle.toLowerCase());
        }

        if (this.filterPrsID) {
           matchResponsible = t.PrsID === this.filterPrsID.PrsID || t.PrsID === this.filterPrsID;
        }
        console.log(this.filterPrsID);
        return matchProject && matchTitle && matchResponsible;
      });
    },

    sprintsByStatus() {
      // Agregar tasks array vacío a cada sprint si no existe
      const planned = this.sprints
        .filter((s) => s.TspStatus === 1)
        .map((s) => ({
          ...s,
          tasks: s.tasks || [],
        }));

      return {
        planned: planned,
        active: this.sprints.filter((s) => s.TspStatus === 2),
        completed: this.sprints.filter((s) => s.TspStatus === 3),
      };
    },

    sprintStats() {
      return {
        planned: this.sprintsByStatus.planned.length,
        active: this.sprintsByStatus.active.length,
        completed: this.sprintsByStatus.completed.length,
        totalPoints: this.sprints.reduce(
          (sum, s) => sum + (s.TspStoryPoints || 0),
          0
        ),
      };
    },
  },

  methods: {
    // Cargar sprints
    async loadSprints() {
      this.loadingSprints = true;
      try {
        const userID = this.$fun.getUserID();
        
        const [sprintsResponse, tasksLoaded] = await Promise.all([
          _sTicSprintService.getAllSprints({}, userID),
          this.loadListTasks()
        ]);

        // Mapear Tasks (backend) a tasks (frontend)
        this.sprints = (sprintsResponse.data || []).map((sprint) => ({
          ...sprint,
          tasks: sprint.Tasks || sprint.tasks || [], // Backend retorna Tasks (mayúscula)
        }));
            } catch (error) {
        this.showSnackbar("Error al cargar sprints: " + error.message, "error");
      } finally {
        this.loadingSprints = false;
      }
    },

    async loadProjects() {
      this.loadingProjects = true;
      try {
        const userID = this.$fun.getUserID();
        const userInfo = this.$fun.getUserInfo();
        const obj = {
          AreID: userInfo?.CtrContract?.AreID ?? 0,
          UsrID: userID,
          TkpStatus: 2,
          IsAdmin: this.$fun.isAdmin() ? 1 : 0
        };
        console.log(obj)
        const response = await _sTaskService.ticproyect(obj, userID);
        
        this.projectOptions = response.data || [];

      } catch (error) {
        this.showSnackbar(
          "Error al cargar proyectos: " + error.message,
          "error"
        );
      } finally {
        this.loadingProjects = false;
      }
    },

    // Abrir diálogo crear sprint
    openSprintDialog() {
      this.editingSprintId = null;
      this.newSprint = {
        TspID: null,
        TspName: "",
        TspStartDate: new Date().toISOString().split("T")[0],
        TspEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0],
        TspStatus: 1,
        TspStoryPoints: 0,
      };
      this.dialogSprint = true;
    },

    // Editar sprint
    editSprint(sprint) {
      this.editingSprintId = sprint.TspID;
      this.newSprint = {
        TspID: sprint.TspID,
        TspName: sprint.TspName,
        TspStartDate: sprint.TspStartDate,
        TspEndDate: sprint.TspEndDate,
        TspStatus: sprint.TspStatus,
        TspStoryPoints: sprint.TspStoryPoints,
      };
      this.dialogSprint = true;
    },

    // Cerrar diálogo sprint
    closeSprintDialog() {
      this.dialogSprint = false;
      this.$refs.sprintForm?.reset();
    },

    // Guardar sprint
    async saveSprint() {
      if (!this.$refs.sprintForm?.validate()) return;

      this.loadingSave = true;
      try {
        const userID = this.$fun.getUserID();
        const response = await _sTicSprintService.save(this.newSprint, userID);

        if (response.data) {
          this.showSnackbar(
            this.editingSprintId
              ? "Sprint actualizado correctamente"
              : "Sprint creado correctamente",
            "success"
          );
          this.closeSprintDialog();
          this.loadSprints();
        }
      } catch (error) {
        this.showSnackbar("Error al guardar sprint: " + error.message, "error");
      } finally {
        this.loadingSave = false;
      }
    },

    // Activar sprint
    async activateSprint(sprint) {
      const confirm = await this.$fun.alert(
        "¿Dese activar el Sprint? <br> No se podra volver a agregar una tarea (Solo Hotfix)",
        "question"
      );
      if (!confirm.value) return;
      // try {
      const userID = this.$fun.getUserID();
      const response = await _sTicSprintService.activate(
        { TspID: sprint.TspID },
        userID
      );

      if (response.data) {
        this.showSnackbar("Sprint activado correctamente", "success");
        this.loadSprints();
      }
      // } catch (error) {
      //     this.showSnackbar('Error al activar sprint: ' + error.message, 'error');
      // }
    },

    // Completar sprint
    async completeSprint(sprint) {
      try {
        const userID = this.$fun.getUserID();
        const response = await _sTicSprintService.complete(
          { TspID: sprint.TspID },
          userID
        );

        if (response.data) {
          this.showSnackbar("Sprint completado correctamente", "success");
          this.loadSprints();
        }
      } catch (error) {
        this.showSnackbar(
          "Error al completar sprint: " + error.message,
          "error"
        );
      }
    },

    // Ver tareas del sprint
    async viewSprintTasks(sprint) {
      this.selectedSprint = sprint;
      this.loadingTasks = true;
      try {
        // Aquí llamarías al servicio para obtener las tareas del sprint
        const userID = this.$fun.getUserID();
        const response = await _sTicSprintService.getTasksBySprint(
          { TspID: sprint.TspID },
          userID
        );
        this.sprintTasks = response.data || [];

        // Por ahora, mostrar un placeholder
        // this.sprintTasks = [];
      } catch (error) {
        this.showSnackbar("Error al cargar tareas: " + error.message, "error");
      } finally {
        this.loadingTasks = false;
        this.dialogSprintTasks = true;
      }
    },

    // Ver detalles del sprint
    viewSprintDetails(sprint) {
      this.selectedSprint = sprint;
      this.sprintTasks = sprint.Tasks || [];
      this.dialogSprintTasks = true;
    },

    // Seleccionar sprint
    selectSprint(sprint) {
      this.selectedSprint = sprint;
      sessionStorage.setItem("activeSprint", JSON.stringify(sprint));
      this.$router.push({ name: "TIC_TASK_KANBAN" });
    },

    // Eliminar sprint
    async deleteSprint(sprint) {
      const confirm = this.$fun.alert(
        "¿Dese eliminar el Sprint? <br> se eliminara toda la informacion relacionada a este",
        "question"
      );
      if (!confirm.value) return;
      // Aquí llamarías al servicio para eliminar el sprint
      // try {
      const userID = this.$fun.getUserID();
      sprint.SecStatus = 0; // Marcar como eliminado
      const response = await _sTicSprintService.save(sprint, userID);
      if (response.data) {
        this.showSnackbar("Sprint eliminado correctamente", "success");
        this.loadSprints();
      }
      // } catch (error) {
      //     this.showSnackbar('Error al eliminar sprint: ' + error.message, 'error
      //     );
    },

    // Utilidades
    formatDate(date) {
      if (!date) return "";
      return this.$moment(date).format("DD/MM/YYYY");
    },

    getStatusColor(status) {
      const colors = {
        1: "info",
        2: "warning",
        3: "success",
        4: "error",
      };
      return colors[status] || "default";
    },

    showSnackbar(message, color = "success") {
      this.snackbar = {
        show: true,
        message,
        color,
      };
    },

    getBaseTaskPayload(userID) {
      const userInfo = this.$fun.getUserInfo();
      const areId = userInfo?.CtrContract?.AreID ?? 0;

      return {
        AreID: areId,
        SecStatus: 1,
        TskStatus: 1,
        UsrCreateID: userID,
      };
    },

    async createQuickTask() {
      const title = (this.quickTaskTitle || "").trim();
      if (!title) return;

      this.loadingQuickTask = true;
      try {
        const userID = this.$fun.getUserID();
        const payload = {
          ...this.getBaseTaskPayload(userID),
          TskTitle: title,
          TkpID: this.quickTaskProjectId || null,
        };

        const response = await _sTaskService.save(payload, userID);
        if (response && response.status === 200) {
          this.showSnackbar("Tarea creada correctamente", "success");
          this.quickTaskTitle = "";
          await this.loadListTasks();
        }
      } catch (error) {
        this.showSnackbar("Error al crear tarea: " + error.message, "error");
      } finally {
        this.loadingQuickTask = false;
      }
    },

    openTaskDialog(task) {
      this.editTask = {
        ...task,
      };
      this.editTaskFile = null;
      this.dialogTaskEdit = true;
    },

    closeTaskDialog() {
      this.dialogTaskEdit = false;
      this.editTask = {};
      this.editTaskFile = null;
      this.$refs.taskForm?.reset();
    },

    async saveTask() {
      if (!this.$refs.taskForm?.validate()) return;

      this.loadingTaskSave = true;
      try {
        const userID = this.$fun.getUserID();
        const payload = {
          ...this.editTask,
          UsrUpdateID: userID,
        };

        if (this.editTaskFile instanceof File) {
          payload.FilePath = this.editTaskFile.name;
          const formData = new FormData();
          formData.append("0", this.editTaskFile);
          await _sGeneral.uploadfile("TicTask", userID, formData, 3);
        }

        const response = await _sTaskService.save(payload, userID);
        if (response && response.status === 200) {
          this.showSnackbar("Tarea actualizada correctamente", "success");
          this.closeTaskDialog();
          await this.loadListTasks();
        }
      } catch (error) {
        this.showSnackbar(
          error.message || "Error al guardar la tarea",
          "error"
        );
      } finally {
        this.loadingTaskSave = false;
      }
    },

    async updateTaskProject(task, projectId) {
      if (!projectId || task.TkpID === projectId) return;

      const previousProjectId = task.TkpID;
      const previousTitle = task.TkpTitle;
      task.TkpID = projectId;

      const project = this.projectOptions.find((p) => p.TkpID === projectId);
      task.TkpTitle = project?.TkpTitle || previousTitle;

      if (!this.savingProjectIds.includes(task.TskID)) {
        this.savingProjectIds.push(task.TskID);
      }

      try {
        const userID = this.$fun.getUserID();
        const payload = {
          ...task,
          UsrUpdateID: userID,
        };
        const response = await _sTaskService.save(payload, userID);
        if (response && response.status === 200) {
          this.showSnackbar("Proyecto actualizado", "success");
        }
      } catch (error) {
        task.TkpID = previousProjectId;
        task.TkpTitle = previousTitle;
        this.showSnackbar(
          error.message || "Error al actualizar proyecto",
          "error"
        );
      } finally {
        this.savingProjectIds = this.savingProjectIds.filter(
          (id) => id !== task.TskID
        );
      }
    },

    isSubtasksOpen(task) {
      return this.expandedTaskIds.includes(task.TskID);
    },

    getSubtaskToggleTitle(task) {
      return this.isSubtasksOpen(task) ? "Ocultar subtareas" : "Ver subtareas";
    },

    getSubtasks(task) {
      return this.subtasksByParent[task.TskID] || [];
    },

    getTaskPointsValue(task) {
      const list = this.subtasksByParent[task.TskID];
      if (Array.isArray(list) && list.length > 0) {
        return list.reduce(
          (sum, subtask) => sum + Number(subtask.TskStoryPoints || 0),
          0
        );
      }
      return Number(task.TskStoryPoints || 0);
    },

    getTaskPointsText(task) {
      const value = this.getTaskPointsValue(task);
      if (value <= 0) return "";
      const list = this.subtasksByParent[task.TskID];
      if (Array.isArray(list) && list.length > 0) {
        return `total ${value} pts`;
      }
      return `${value} pts`;
    },

    hasSubtasks(task) {
      const list = this.subtasksByParent[task.TskID];
      if (Array.isArray(list)) {
        return list.length > 0;
      }
      return !!task.hasSubtasks;
    },

    isTaskDragDisabled(task) {
      return this.hasSubtasks(task);
    },

    async toggleSubtasks(task) {
      if (this.isSubtasksOpen(task)) {
        this.expandedTaskIds = this.expandedTaskIds.filter(
          (id) => id !== task.TskID
        );
        return;
      }

      this.expandedTaskIds.push(task.TskID);
      if (!this.subtasksByParent[task.TskID]) {
        await this.loadSubtasks(task);
      }
    },

    async loadSubtasks(task) {
      if (this.loadingSubtaskIds.includes(task.TskID)) return;

      this.loadingSubtaskIds.push(task.TskID);
      try {
        const userID = this.$fun.getUserID();
        const response = await _sTaskService.subTasksByParent(
          { TskParentID: task.TskID },
          userID
        );
        const list = response.data || [];
        this.$set(this.subtasksByParent, task.TskID, list);
        task.hasSubtasks = list.length > 0;
      } catch (error) {
        this.showSnackbar(
          "Error al cargar subtareas: " + error.message,
          "error"
        );
      } finally {
        this.loadingSubtaskIds = this.loadingSubtaskIds.filter(
          (id) => id !== task.TskID
        );
      }
    },

    onSubtaskDragStart(task) {
      if (this.isTaskDragDisabled(task)) {
        this.showSnackbar(
          "No puedes mover una tarea con subtareas",
          "warning"
        );
        return;
      }
      this.draggingTask = task;
    },

    onSubtaskDragEnd() {
      this.draggingTask = null;
      this.dropTargetId = null;
    },

    onSubtaskDragOver(task) {
      if (!this.canDropOnTask(task)) return;
      this.dropTargetId = task.TskID;
    },

    onSubtaskDragEnter(task) {
      if (!this.canDropOnTask(task)) return;
      this.dropTargetId = task.TskID;
    },

    onSubtaskDragLeave(task) {
      if (this.dropTargetId === task.TskID) {
        this.dropTargetId = null;
      }
    },

    canDropOnTask(parentTask) {
      if (!this.draggingTask) return false;
      if (this.draggingTask.TskID === parentTask.TskID) return false;
      if (parentTask.TskParentID) return false;
      if (this.hasSubtasks(this.draggingTask)) return false;
      return true;
    },

    async onSubtaskDrop(parentTask) {
      if (!this.draggingTask) return;
      if (!this.canDropOnTask(parentTask)) {
        this.showSnackbar(
          "Solo puedes soltar en tareas de primer nivel",
          "warning"
        );
        this.dropTargetId = null;
        return;
      }

      const childTask = this.draggingTask;
      this.draggingTask = null;
      this.dropTargetId = null;
      await this.assignSubtask(childTask, parentTask);
    },

    async assignSubtask(childTask, parentTask) {
      const userID = this.$fun.getUserID();
      const previousParentId = childTask.TskParentID || null;
      const payload = {
        ...childTask,
        TskParentID: parentTask.TskID,
        UsrUpdateID: userID,
      };

      try {
        const response = await _sTaskService.save(payload, userID);
        if (response && response.status === 200) {
          childTask.TskParentID = parentTask.TskID;
          this.unassignedTasks = this.unassignedTasks.filter(
            (t) => t.TskID !== childTask.TskID
          );

          if (!this.subtasksByParent[parentTask.TskID]) {
            this.$set(this.subtasksByParent, parentTask.TskID, []);
          }
          const exists = this.subtasksByParent[parentTask.TskID].some(
            (t) => t.TskID === childTask.TskID
          );
          if (!exists) {
            this.subtasksByParent[parentTask.TskID].push(childTask);
          }
          parentTask.hasSubtasks = true;

          if (previousParentId) {
            const prevList = this.subtasksByParent[previousParentId] || [];
            this.subtasksByParent[previousParentId] = prevList.filter(
              (t) => t.TskID !== childTask.TskID
            );
          }

          this.showSnackbar("Subtarea asignada", "success");
        }
      } catch (error) {
        this.showSnackbar(
          error.message || "Error al asignar subtarea",
          "error"
        );
      }
    },

    async removeSubtaskParent(subtask, parentTask) {
      const userID = this.$fun.getUserID();
      const payload = {
        ...subtask,
        TskParentID: null,
        UsrUpdateID: userID,
      };

      try {
        const response = await _sTaskService.save(payload, userID);
        if (response && response.status === 200) {
          subtask.TskParentID = null;
          this.unassignedTasks.unshift(subtask);
          const list = this.subtasksByParent[parentTask.TskID] || [];
          this.subtasksByParent[parentTask.TskID] = list.filter(
            (t) => t.TskID !== subtask.TskID
          );
          parentTask.hasSubtasks =
            this.subtasksByParent[parentTask.TskID].length > 0;
          this.showSnackbar("Subtarea movida a nivel raiz", "success");
        }
      } catch (error) {
        this.showSnackbar(
          error.message || "Error al quitar subtarea",
          "error"
        );
      }
    },

    async loadListTasks() {
      try {
        const userID = this.$fun.getUserID();
        const obj = {
          TskStatus: null,
          UsrID: null,
        };
        const response = await _sTaskService.tictaskBystatusTask(obj, userID);
        console.log("response", response);
        // Estas son solo las tareas del backlog (sin asignar)
        this.tasks = response.data || [];
        this.unassignedTasks = this.tasks;

      } catch (error) {
        this.showSnackbar("Error al cargar tareas: " + error.message, "error");
      }
    },

    // Evento cuando se suelta una tarea en un sprint
    async onTaskDrop(event, sprint) {

      if (event.added) {
        const addedTask = event.added.element;

        // Llamar endpoint para guardar la asignación
        try {
          await this.saveTaskToSprint(addedTask.TskID, sprint.TspID);

          this.showSnackbar(
            `Tarea #${addedTask.TskID} asignada al sprint "${sprint.TspName}"`,
            "success"
          );

          // Remover de unassignedTasks si existe
          const index = this.unassignedTasks.findIndex(
            (t) => t.TskID === addedTask.TskID
          );
          if (index > -1) {
            this.unassignedTasks.splice(index, 1);
          }
        } catch (error) {
          // Si hay error, remover la tarea del sprint en el UI
          const taskIndex = sprint.tasks.findIndex(
            (t) => t.TskID === addedTask.TskID
          );
          if (taskIndex > -1) {
            sprint.tasks.splice(taskIndex, 1);
          }
          this.showSnackbar(
            error.message || "Error al asignar tarea al sprint",
            "error"
          );
        }
      }
    },

    // Remover tarea del sprint
    async removeTaskFromSprint(task, sprint) {
      try {
        const userID = this.$fun.getUserID();
        const response = await _sTicSprintService.removeTaskFromSprint(
          {
            TspID: sprint.TspID,
            TskID: task.TskID,
          },
          userID
        );

        if (response.data) {
          const index = sprint.tasks.findIndex((t) => t.TskID === task.TskID);
          if (index > -1) {
            sprint.tasks.splice(index, 1);
            task.TspID = null; // Desasociar del sprint
            
            this.unassignedTasks.push(task);
          }

          this.showSnackbar(
            `Tarea #${task.TskID} removida del sprint`,
            "success"
          );
        }
      } catch (error) {
        this.showSnackbar(
          error.message || "Error al remover tarea del sprint",
          "error"
        );
      }
    },

    // Guardar asignación de tarea a sprint
    async saveTaskToSprint(taskId, sprintId) {
      try {
        const userID = this.$fun.getUserID();
        const response = await _sTicSprintService.assignTaskToSprint(
          {
            TskID: taskId,
            TspID: sprintId,
          },
          userID
        );

        if (!response.data) {
          throw new Error("No se recibió respuesta del servidor");
        }

        return response.data;
      } catch (error) {
        throw error;
      }
    },
  },

  created() {
    
    // ⚡ Ejecutar ambas cargas en paralelo (no secuencial)
    Promise.all([
      this.loadProjects(),
      this.loadSprints()
    ]).catch(error => {
    });
  },

  mounted() {
  },
};
</script>

<style scoped>
.backlog-container {
  background: #f5f5f5;
  min-height: 100vh;
  padding: 20px 0;
}

.sprint-panel {
  background: #ffffff;
  border-radius: 10px;
  padding: 1.25rem;
  border: 1px solid #e8ecf0;
  height: fit-content;
}

.sprint-panel-backlog {
  background: #ffffff;
  border-radius: 10px;
  padding: 1.25rem;
  border: 1px solid #e8ecf0;
  height: stretch;
}

.column-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  margin-bottom: 14px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
}

.sprint-list {
  overflow-y: auto;
}

.sprint-list-tall {
  max-height: 520px;
}

.sprint-list-medium {
  max-height: 300px;
}

.sprint-list-compact {
  max-height: 220px;
}

/* Tarjetas de Tareas en Backlog */
.task-list-backlog {
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
}

.task-card {
  cursor: move;
  transition: all 0.2s ease;
  border-left: 3px solid #2196f3;
}

.task-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.task-content {
  overflow: hidden;
}

/* Tarjetas de Sprint con Drop Zone */
.sprint-card-droppable {
  cursor: default;
  transition: all 0.3s ease;
  border-left: 4px solid #2196f3;
}

.sprint-card-droppable:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.sprint-title-header {
  width: 100%;
}

/* Drop Zone */
.task-drop-zone {
  min-height: 120px;
  max-height: 400px;
  overflow-y: auto;
  padding: 4px;
  border: 2px dashed transparent;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.task-drop-zone:empty {
  border-color: #e0e0e0;
}

.drop-zone-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}

/* Tarjetas de Tareas dentro del Sprint */
.task-card-sprint {
  cursor: move;
  transition: all 0.2s ease;
  border-left: 3px solid #4caf50;
  background: #f9fffe;
}

.task-card-sprint:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  transform: translateX(2px);
}

/* Ghost effect durante drag */
.ghost {
  opacity: 0.5;
  background: #e3f2fd;
  border: 2px dashed #2196f3;
}

.sprint-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid #2196f3;
}

.sprint-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.active-sprint {
  border-left: 4px solid #ff9800;
  background: linear-gradient(135deg, #fffbee 0%, #fff 100%);
}

.completed-sprint {
  border-left: 4px solid #4caf50;
  background: linear-gradient(135deg, #f1f8e9 0%, #fff 100%);
  opacity: 0.8;
}

.sprint-title {
  color: #333;
  font-size: 15px;
  word-break: break-word;
}

.sprint-dates {
  color: #666;
}

.sprint-points {
  display: flex;
  gap: 8px;
}

.sprint-actions {
  display: flex;
  gap: 8px;
  padding-top: 8px;
}

.sprint-badge {
  margin: 8px 0;
}

.progress-bar {
  margin: 12px 0;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-radius: 8px 8px 0 0 !important;
}

.white--text {
  color: white !important;
}

.primary {
  background-color: #1976d2 !important;
}

/* Scrollbar styling */
.task-list-backlog::-webkit-scrollbar,
.task-drop-zone::-webkit-scrollbar,
.sprint-list::-webkit-scrollbar {
  width: 6px;
}

.task-list-backlog::-webkit-scrollbar-track,
.task-drop-zone::-webkit-scrollbar-track,
.sprint-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.task-list-backlog::-webkit-scrollbar-thumb,
.task-drop-zone::-webkit-scrollbar-thumb,
.sprint-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.task-list-backlog::-webkit-scrollbar-thumb:hover,
.task-drop-zone::-webkit-scrollbar-thumb:hover,
.sprint-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Backlog minimal styles */
.backlog-card {
  border: 1px solid #e8ecf0 !important;
  background: #fff;
}

.backlog-header {
  background: #fafbfc;
}

.backlog-title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.4px;
  color: #374151;
  text-transform: uppercase;
}

.backlog-sub {
  font-size: 11px;
  color: #94a3b8;
  letter-spacing: 0.2px;
}

.count-chip {
  font-size: 11px !important;
  font-weight: 600;
  height: 18px !important;
}

/* Quick add */
.quick-add {
  gap: 8px;
}

.quick-add-input {
  flex: 1 1 auto;
}

.quick-add-select {
  width: 220px;
  min-width: 180px;
}

.task-project-select {
  max-width: 200px;
  min-width: 140px;
}

.task-rows {
  max-height: 250px;
  overflow-y: auto;
}

.task-item {
  display: block;
}

.drop-target {
  background: #eef2ff;
  border: 1px dashed #7c3aed;
}

.subtask-drag-handle {
  cursor: grab;
}

.subtasks-panel {
  background: #fbfbfd;
  border-left: 2px solid #e2e8f0;
  padding: 6px 8px;
  border-radius: 6px;
}

.subtask-row {
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #475569;
  padding: 4px 0;
}

.subtask-title {
  font-size: 12.5px;
}

.subtask-id {
  font-size: 11px;
  color: #94a3b8;
  font-family: monospace;
}

.subtasks-empty {
  font-size: 12px;
  color: #94a3b8;
  padding: 4px 0;
}

/* Task rows */
.task-row {
  cursor: grab;
  transition: background 0.12s ease;
  background: transparent;
  border: #1976d2 1px solid ;
}

.task-row:hover {
  background: #f8fafc;
}

.task-row:hover .drag-handle {
  opacity: 1 !important;
}

.drag-handle {
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.task-title {
  font-size: 13px;
  color: #1e293b;
  font-weight: 500;
  max-width: 300px;
}

.task-meta {
  font-size: 11.5px;
  color: #94a3b8;
  max-width: 180px;
}

.task-id {
  font-size: 11px;
  color: #cbd5e1;
  font-family: monospace;
  flex-shrink: 0;
}

/* Ghost drag */
.ghost {
  opacity: 0.4;
  background: #f1f5f9;
  border-radius: 8px;
}

/* Empty */
.empty-state {
  pointer-events: none;
}
.empty-text {
  font-size: 12px;
  color: #94a3b8;
}

/* ── Page header ── */
.backlog-page-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.backlog-page-sub {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
  letter-spacing: 0.1px;
}

/* ── Stats toggle button ── */
.stats-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 13px;
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  outline: none;
}

.stats-toggle-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

/* ── Sprint statistics strip ── */
.stats-strip {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.stat-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px 16px;
  min-width: 155px;
  flex: 1;
  border-left-width: 3px;
  transition: box-shadow 0.15s;
}

.stat-pill:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.stat-pill--blue  { border-left-color: #3b82f6; }
.stat-pill--amber { border-left-color: #f59e0b; }
.stat-pill--green { border-left-color: #22c55e; }
.stat-pill--violet{ border-left-color: #8b5cf6; }

.stat-pill__value {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.stat-pill__label {
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;
  white-space: nowrap;
}
</style>
